import * as Sentry from "@sentry/browser";
import type { ErrorEvent, EventHint, Integration } from "@sentry/core";

export const initSentry = (env: Partial<TracingConfig>): void => {
  if (env.sentryDSN === undefined) {
    return;
  }

  const integrations: Integration[] = [];

  if (env.sentryReplayEnabled === true) {
    integrations.push(Sentry.replayIntegration());
  }

  if (env.sentryProfilingEnabled === true) {
    // Enables performance, profiling and insights monitoring
    integrations.push(Sentry.browserTracingIntegration());
    integrations.push(Sentry.browserProfilingIntegration());
  }

  Sentry.init({
    enabled: env.tracingEnabled,
    dsn: env.sentryDSN,
    integrations,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: env.sentryTracesSampleRate,
    environment: env.envName,
    release: env.tracingRelease,
    replaysOnErrorSampleRate: env.sentryReplaysOnErrorSampleRate ?? 1.0,
    replaysSessionSampleRate: env.sentryReplaysSessionSampleRate ?? 1.0,

    ignoreErrors: env.sentryIgnoreErrors,
    beforeSend(
      event: ErrorEvent,
      hint: EventHint,
    ): PromiseLike<ErrorEvent | null> | ErrorEvent | null {
      if (isNonErrorException(event)) {
        return processNonErrorException(event);
      }
      return event;
    },
  });
};

function isNonErrorException(event: Sentry.Event): boolean {
  return (
    event.exception?.values?.[0]?.value?.startsWith(
      "Non-Error exception captured",
    ) ?? false
  );
}

function processNonErrorException(event: ErrorEvent): ErrorEvent {
  /**
   * Processes a Sentry event to handle non-error exceptions.
   *
   * This function checks if the event contains serialized error data.
   * If so, it extracts the real error message from the serialized data
   * and updates the event's exception value and message with it.
   *
   * This is particularly useful for handling cases where non-error
   * exceptions are captured, which may not provide a stack trace or
   * detailed error information by default.
   *
   * For more information, see:
   * [Events with non-error exception](https://docs.sentry.io/platforms/javascript/guides/angular/troubleshooting/#events-with-non-error-exception)
   */

  const serialized = event.extra?.__serialized__;
  if (typeof serialized !== "object" || serialized === null) {
    return event;
  }

  const error =
    "error" in serialized && typeof serialized.error === "object"
      ? serialized.error
      : null;

  let message = null;
  if (
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    message = error.message;
  } else if (
    "message" in serialized &&
    typeof serialized.message === "string"
  ) {
    message = serialized.message;
  }

  if (
    message !== null &&
    message !== undefined &&
    event.exception?.values?.[0] !== undefined
  ) {
    event.exception.values[0].value = message;
    event.message = message;
  }
  return event;
}

interface TracingConfig {
  tracingEnabled: boolean;
  trackingEnabled: boolean;
  tracingRelease: string;
  envName: string;

  sentryDSN: string;
  sentryTracesSampleRate: number;
  sentryReplaysOnErrorSampleRate?: number;
  sentryReplaysSessionSampleRate?: number;
  sentryIgnoreErrors?: string[];
  sentryProfilingEnabled?: boolean;
  sentryReplayEnabled?: boolean;

  awsRumApplicationId: string;

  tracingLogging: boolean;
}
